import { useEffect, useState } from "react";
import { Instagram, Heart, MessageCircle, Send, BadgeCheck, Twitter, Facebook, Youtube } from "lucide-react";
import { api } from "@/lib/api";

function TikTokIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}

const PLATFORMS = {
  instagram: { label: "Instagram", icon: Instagram },
  tiktok: { label: "TikTok", icon: TikTokIcon },
  twitter: { label: "Twitter", icon: Twitter },
  facebook: { label: "Facebook", icon: Facebook },
  youtube: { label: "YouTube", icon: Youtube },
};

export default function SocialBlock() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/social/posts").then((r) => setPosts(r.data?.items || [])).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section data-testid="social-block">
      <h2 className="font-headline uppercase text-xl md:text-2xl mb-4 flex items-center gap-2">
        <span className="live-dot" /> DE REDES
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {posts.map((p, i) => {
          const meta = PLATFORMS[p.platform] || PLATFORMS.instagram;
          const Icon = meta.icon;
          return (
            <article
              key={i}
              className="rounded-sm border overflow-hidden card-hover"
              style={{ borderColor: "var(--border)", background: "var(--bg)" }}
              data-testid={`social-card-${p.platform}-${i}`}
            >
              <div className="flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-orange-400" />
                  <div>
                    <div className="text-sm font-bold flex items-center gap-1">
                      {p.handle}
                      {p.verified && <BadgeCheck size={14} className="text-blue-500" fill="currentColor" />}
                    </div>
                    <div className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>
                      {meta.label}{p.time ? ` · ${p.time}` : ""}
                    </div>
                  </div>
                </div>
                <Icon size={18} style={{ color: "var(--text-muted)" }} />
              </div>
              {p.image && (
                <div className="aspect-square bg-black">
                  <img src={p.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
              )}
              <div className="p-3">
                {(p.likes || p.comments || p.shares) && (
                  <div className="flex items-center gap-4 text-sm font-semibold mb-2" style={{ color: "var(--text)" }}>
                    {p.likes && <span className="flex items-center gap-1"><Heart size={14} /> {p.likes}</span>}
                    {p.comments && <span className="flex items-center gap-1"><MessageCircle size={14} /> {p.comments}</span>}
                    {p.shares && <span className="flex items-center gap-1"><Send size={14} /> {p.shares}</span>}
                  </div>
                )}
                <p className="text-sm leading-snug mb-2" style={{ color: "var(--text)" }}>{p.text}</p>
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold accent-text hover:underline">
                    Ver en {meta.label} →
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
